import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { api, unwrap } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import WhatsAppFAB from "@/components/shared/WhatsAppFAB";
import { openReceiptWindow } from "@/components/shared/BookingReceipt";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;
const POLL_MAX_TICKS = Math.ceil(POLL_TIMEOUT_MS / POLL_INTERVAL_MS);

const PAID_LIKE = new Set(["paid", "assigned", "in_progress", "completed"]);

/**
 * Animated SVG checkmark. Reveals via `pathLength` once `show` flips true.
 */
function AnimatedCheckmark({ show }) {
  return (
    <motion.div
      initial={{ scale: 0.4, opacity: 0 }}
      animate={show ? { scale: 1, opacity: 1 } : { scale: 0.6, opacity: 0.4 }}
      transition={{ type: "spring", duration: 0.7 }}
      className="w-24 h-24 rounded-full bg-[#10B981]/10 grid place-items-center mx-auto"
      data-testid="confirmation-success-icon"
    >
      <svg width="64" height="64" viewBox="0 0 52 52" fill="none">
        <motion.circle
          cx="26" cy="26" r="23"
          stroke="#10B981" strokeWidth="3"
          initial={{ pathLength: 0 }}
          animate={show ? { pathLength: 1 } : { pathLength: 0 }}
          transition={{ duration: 0.7 }}
        />
        <motion.path
          d="M14 27 L23 36 L39 18"
          stroke="#10B981" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={show ? { pathLength: 1 } : { pathLength: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        />
      </svg>
    </motion.div>
  );
}

function PendingSkeleton({ secondsElapsed }) {
  return (
    <Card className="rounded-2xl border border-amber-200 bg-amber-50/40 mt-8 p-6" data-testid="confirmation-pending-skeleton">
      <div className="flex items-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin text-amber-700 shrink-0" />
        <div>
          <div className="font-display font-bold text-amber-900">Confirming your payment…</div>
          <div className="text-sm text-amber-800/80 mt-0.5">This usually takes 3–10 seconds. Hold tight.</div>
        </div>
      </div>
      <div className="mt-5 space-y-2 animate-pulse">
        <div className="h-3 w-32 bg-amber-200/60 rounded" />
        <div className="h-7 w-48 bg-amber-200/60 rounded" />
        <div className="h-px bg-amber-200/60 my-3" />
        <div className="flex justify-between">
          <div className="h-3 w-12 bg-amber-200/60 rounded" />
          <div className="h-5 w-20 bg-amber-200/60 rounded" />
        </div>
      </div>
      {secondsElapsed >= 15 && (
        <p className="text-xs text-amber-800 mt-4">
          Still processing — if it takes longer than 30 seconds, check your inbox or <Link to="/contact" className="underline font-semibold">contact us</Link>.
        </p>
      )}
    </Card>
  );
}

export default function ConfirmationPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { t } = useI18n();

  const [booking, setBooking] = useState(null);
  const [ticks, setTicks] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const stopRef = useRef(false);

  const isPaid = booking && PAID_LIKE.has(booking.status);
  const isLoading = !booking;
  const showPending = booking && booking.status === "pending_payment" && !timedOut;

  // 1) Initial booking fetch (always)
  useEffect(() => {
    let cancelled = false;
    api.get(`/bookings/${id}/public`).then((r) => {
      if (!cancelled) setBooking(unwrap(r));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  // 2) Polling — runs while we know the booking exists but is still pending_payment.
  //    Works WITH or WITHOUT a session_id (covers users coming from the email link).
  useEffect(() => {
    if (!booking || isPaid || timedOut) return;
    stopRef.current = false;
    let timer;

    const tick = async () => {
      if (stopRef.current) return;

      // If we have a session id, prefer the Stripe status endpoint (also triggers
      // the server-side webhook fallback that flips the booking to paid).
      try {
        if (sessionId) {
          await api.get(`/payments/status/${sessionId}`).catch(() => {});
        }
        const r = await api.get(`/bookings/${id}/public`);
        const data = unwrap(r);
        setBooking(data);
        if (PAID_LIKE.has(data.status)) {
          stopRef.current = true;
          return;
        }
      } catch {}

      setTicks((n) => {
        const next = n + 1;
        if (next >= POLL_MAX_TICKS) {
          setTimedOut(true);
          stopRef.current = true;
          return next;
        }
        timer = setTimeout(tick, POLL_INTERVAL_MS);
        return next;
      });
    };
    timer = setTimeout(tick, POLL_INTERVAL_MS);
    return () => { stopRef.current = true; clearTimeout(timer); };
  }, [booking, id, sessionId, isPaid, timedOut]);

  const secondsElapsed = Math.round((ticks * POLL_INTERVAL_MS) / 1000);

  const [receiptLoading, setReceiptLoading] = useState(false);
  const handlePrintReceipt = async () => {
    if (!booking?.booking_number || !booking?.receipt_token) return;
    setReceiptLoading(true);
    try {
      const r = await api.get(`/bookings/${booking.booking_number}/receipt`, {
        params: { token: booking.receipt_token },
      });
      openReceiptWindow(unwrap(r));
    } catch {
      toast.error(t("confirmation.receipt_error"));
    } finally {
      setReceiptLoading(false);
    }
  };

  let title = t("confirmation.title");
  let subtitle = t("confirmation.subtitle");
  if (isLoading) { title = " "; subtitle = " "; }
  else if (showPending) { title = t("confirmation.polling"); subtitle = ""; }
  else if (timedOut && !isPaid) { title = "Hmm…"; subtitle = t("confirmation.failed"); }

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="confirmation-page">
      <SEO
        title={booking?.booking_number ? `Booking confirmed ${booking.booking_number} — Ever Clean Home` : "Booking confirmation — Ever Clean Home"}
        description="Your Ever Clean Home cleaning is confirmed. We'll email a copy of the receipt and reach out to coordinate the schedule."
        noindex
      />
      <Header />
      <WhatsAppFAB />
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <AnimatedCheckmark show={!!isPaid} />

        <motion.h1
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="mt-6 text-center font-display text-4xl md:text-5xl font-extrabold tracking-tighter"
          data-testid="confirmation-title"
        >
          {isLoading ? <span className="inline-block h-10 w-72 rounded-lg bg-[#E5E7EB] animate-pulse align-middle" /> : title}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="mt-3 text-center text-[#4B5563]"
        >
          {isLoading ? <span className="inline-block h-4 w-96 max-w-full rounded bg-[#E5E7EB] animate-pulse" /> : subtitle}
        </motion.p>

        {showPending && <PendingSkeleton secondsElapsed={secondsElapsed} />}

        {booking && isPaid && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}>
            <Card className="rounded-2xl border border-[#E5E7EB] mt-8 p-6 text-center" data-testid="confirmation-booking-card">
              <div className="text-xs uppercase tracking-wider font-bold text-[#4B5563]">{t("confirmation.booking_number")}</div>
              <div className="mt-2 font-mono text-3xl font-bold text-[#0EA5A4] select-all" data-testid="confirmation-booking-number">
                {booking.booking_number}
              </div>
              <div className="mt-5 pt-5 border-t border-[#E5E7EB] flex items-center justify-between">
                <span className="text-sm text-[#4B5563]">{t("checkout.total")}</span>
                <span className="font-mono text-2xl font-bold">${booking.total.toFixed(2)}</span>
              </div>
            </Card>
            <p className="mt-6 text-center text-sm text-[#4B5563]">{t("confirmation.coordinate")}</p>
          </motion.div>
        )}

        {!isLoading && (
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
            {booking && isPaid && (
              <>
                <Button onClick={handlePrintReceipt} disabled={receiptLoading}
                  variant="outline" className="rounded-xl" data-testid="receipt-btn">
                  {receiptLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("confirmation.print_receipt")}
                </Button>
                <Link to={`/track/${booking.booking_number}`}>
                  <Button variant="outline" className="rounded-xl" data-testid="track-btn">{t("confirmation.track")}</Button>
                </Link>
              </>
            )}
            <Link to="/book">
              <Button className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="book-another-btn">
                {t("confirmation.book_another")} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="rounded-xl" data-testid="home-btn">{t("confirmation.home")}</Button>
            </Link>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
